import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	useReflagCompetitorsMutation,
	useUnflagCompetitorsMutation,
} from "@/pages/Private/redux/organization/organization.api";

import { ContactSelectType } from "../../Contacts/schema/contacts";

export interface CompetitorSelectionPayload {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
}

interface UseCompetitorUnflagParams {
	/** The bulk selection the footer action applies to. */
	getSelectionPayload: () => CompetitorSelectionPayload;
	clearSelection: () => void;
	openSnackbar: (message: string, autoHideDuration?: number) => void;
}

/** What the confirm modal is currently asking about — a row, or the selection. */
export interface PendingUnflag {
	organizationId?: number;
	companyName?: string;
	count: number;
}

/**
 * The "Not a competitor" flow: confirm, un-flag, then offer the undo.
 *
 * The undo re-flags exactly the organizations the API reports back, so it is
 * unaffected by the selection having changed (or a filter-wide selection having
 * matched something else) in the meantime.
 */
export const useCompetitorUnflag = ({
	getSelectionPayload,
	clearSelection,
	openSnackbar,
}: UseCompetitorUnflagParams) => {
	const { t } = useTranslation();
	const [pending, setPending] = useState<PendingUnflag | null>(null);
	const [undoableIds, setUndoableIds] = useState<number[]>([]);

	const [unflagCompetitors, { isLoading: isUnflagging }] = useUnflagCompetitorsMutation();
	const [reflagCompetitors] = useReflagCompetitorsMutation();

	const askForRow = useCallback((organizationId: number, companyName?: string) => {
		setPending({ organizationId, companyName, count: 1 });
	}, []);

	const askForSelection = useCallback((count: number) => {
		setPending({ count });
	}, []);

	const cancel = useCallback(() => setPending(null), []);

	const undo = useCallback(
		async (organizationIds: number[]) => {
			if (organizationIds.length === 0) {
				return;
			}

			try {
				await reflagCompetitors({
					selectType: ContactSelectType.CURRENT_PAGE,
					ids: organizationIds,
				}).unwrap();
				setUndoableIds([]);
				openSnackbar(t("contacts.competitors.reflagDone"));
			} catch {
				openSnackbar(t("basics.error"));
			}
		},
		[reflagCompetitors, openSnackbar, t]
	);

	const confirm = useCallback(
		async (scoreCompanyCategoryId: number) => {
			if (!pending) {
				return;
			}

			const payload = pending.organizationId
				? { selectType: ContactSelectType.CURRENT_PAGE, ids: [pending.organizationId] }
				: getSelectionPayload();

			try {
				const result = await unflagCompetitors({
					...payload,
					scoreCompanyCategoryId,
				}).unwrap();

				setUndoableIds(result.organizationIds ?? []);

				if (!pending.organizationId) {
					clearSelection();
				}

				openSnackbar(t("contacts.competitors.unflagDone", { count: result.count }), 8000);
			} catch {
				openSnackbar(t("basics.error"));
			} finally {
				setPending(null);
			}
		},
		[pending, getSelectionPayload, unflagCompetitors, clearSelection, openSnackbar, t]
	);

	return {
		pending,
		isUnflagging,
		undoableIds,
		askForRow,
		askForSelection,
		cancel,
		confirm,
		undo,
	};
};
